import { T, formatMoney, tt } from '@agos/shared'

import {
  LUNCH_SECTIONS,
  dayShortDate,
  lunchMealDescription,
  type LunchLinkedProduct,
  type LunchSettings,
  type LunchWeek,
} from '@agos/shared'

import { useEffect, useRef, useState } from 'react'

import { LunchPrintWeek } from './LunchPrintWeek'
import {
  PRINT_MEASURE_WIDTH_PX,
  printDayScale,
  printDayWidth,
  printFullDate,
} from './print-week'

/**
 * The printed menu — the sheet that gets pinned up in the restaurant every
 * week. FRONTEND.md §8 counts this as an output, not decoration.
 *
 * It renders inside a `print:block hidden` wrapper: invisible on screen,
 * and the only thing on the page once the browser prints, because
 * `packages/ui`'s print rules hide the nav, the sidebar and the header.
 */

/** Legacy's print accent, kept: this is what the restaurant's menus look like. */
/**
 * What the lunch menu tells the printer — §9.132, the owner's point 16b.
 *
 * <p><b>`@page` pins the sheet.</b> Without it the browser picks its own — often Letter, with its own
 * margins — so the same menu prints differently on different machines and none of them is the one
 * this template was designed against. 10 mm is legacy's margin.
 *
 * <p><b>`print-color-adjust` is what keeps the accent.</b> Browsers drop background and accent colour
 * when printing unless told not to, so a template built around `PRINT_ACCENT` came out grey.
 *
 * <p>Named rather than inlined so it can be asserted on: neither rule has any effect on screen or any
 * DOM state, and jsdom does not implement print media, so *present and correct* is the whole of what
 * a test can check.
 */
export const PRINT_PAGE_CSS = `
  @page { size: A4 portrait; margin: 10mm; }
  @media print {
    body { background: white !important; color: black !important; }
    *, *::before, *::after {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`

const PRINT_ACCENT = '#c0392b'
const PRINT_ACCENT_LIGHT = '#f9eae8'

function sectionLabel(settings: LunchSettings, type: string): string {
  const custom = settings.sectionLabels?.[type]
  if (custom) return custom
  return (
    LUNCH_SECTIONS.find((section) => section.type === type)?.label ??
    settings.customSections?.find((section) => section.type === type)?.label ??
    type
  )
}

function PrintHeader({ settings, subtitle }: { settings: LunchSettings; subtitle?: string }) {
  return (
    <header
      className="mb-4 pb-3 border-b-2"
      style={{ borderColor: PRINT_ACCENT }}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black" style={{ color: PRINT_ACCENT }}>
            {settings.restaurantName}
          </h1>
          {settings.restaurantTagline && (
            <p className="text-xs italic text-gray-600">{settings.restaurantTagline}</p>
          )}
          {subtitle && <p className="text-sm font-bold mt-1">{subtitle}</p>}
        </div>
        {settings.restaurantLogoUrl && (
          <img src={settings.restaurantLogoUrl} alt="" className="h-12 w-auto object-contain" />
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-gray-600">
        {settings.restaurantAddress && <span>{settings.restaurantAddress}</span>}
        {settings.restaurantPhone && <span>{settings.restaurantPhone}</span>}
        {settings.restaurantEmail && <span>{settings.restaurantEmail}</span>}
        <span>
          <T>Obedy</T> {settings.timeFrom}–{settings.timeTo}
        </span>
      </div>
    </header>
  )
}

function PrintDay({
  day,
  settings,
  weekStartDate,
  products,
}: {
  day: LunchWeek['days'][number]
  settings: LunchSettings
  weekStartDate: string | undefined
  products: readonly LunchLinkedProduct[]
}) {
  if (!day.enabled || day.meals.length === 0) return null

  const shortDate = dayShortDate(weekStartDate, day.dayKey)
  const byType = new Map<string, typeof day.meals>()
  for (const meal of day.meals) {
    byType.set(meal.type, [...(byType.get(meal.type) ?? []), meal])
  }

  return (
    <section className="mb-3 break-inside-avoid">
      <h2
        className="text-sm font-black uppercase tracking-wide px-2 py-1 rounded"
        style={{ backgroundColor: PRINT_ACCENT_LIGHT, color: PRINT_ACCENT }}
      >
        {day.dayName}
        {shortDate && <span className="font-normal"> · {shortDate}</span>}
      </h2>

      <div className="mt-1.5 space-y-1.5">
        {[...byType.entries()].map(([type, meals]) => (
          <div key={type}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
              {sectionLabel(settings, type)}
            </p>
            {meals.map((meal) => (
              <div key={meal.id} className="flex items-baseline justify-between gap-3 text-xs">
                <span className="flex-1">
                  <span className="font-semibold">{meal.name}</span>
                  {meal.allergens.length > 0 && (
                    <sup className="text-[9px] text-gray-500 ml-0.5">
                      {meal.allergens.join(',')}
                    </sup>
                  )}
                  {/* §9.401 - the linked product's popis, never the stored copy. */}
                  {lunchMealDescription(meal, products) !== '' && (
                    <span className="text-gray-500 italic">
                      {' '}
                      - {lunchMealDescription(meal, products)}
                    </span>
                  )}
                </span>
                <span className="font-bold whitespace-nowrap" style={{ color: PRINT_ACCENT }}>
                  {formatMoney(meal.price)}
                  {meal.bundlePrice != null && (
                    <span className="font-normal text-gray-500">
                      {' '}
                      / {formatMoney(meal.bundlePrice)} <T>k menu</T>
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  )
}

/**
 * One day's sheet, as its own component - §9.382.
 *
 * **Rendered twice on purpose**: once hidden, to be measured, and once scaled, to be printed.
 * Measuring something that is not what prints is the way this fix quietly stops working, so there is
 * one definition of the content and both copies are it.
 */
function PrintDayContent({
  week,
  settings,
  days,
  products,
}: {
  week: LunchWeek
  settings: LunchSettings
  days: LunchWeek['days']
  products: readonly LunchLinkedProduct[]
}) {
  const day = days[0]
  const fullDate = day === undefined ? '' : printFullDate(week.startDate, day.dayKey)

  return (
    <>
      {/* §9.386, FB-687 - **legacy's day sheet, element for element.** §9.377 rebuilt the week 1:1
        * and said day mode kept AGOS's template; the owner has asked for this document twice
        * (*"1:1 to old system"*, then *"do obedové menu properly"*). The header keeps the brand,
        * the contact line and the logo, which legacy's day sheet also draws - what moves is the day
        * name, which belongs to the bar below rather than to a subtitle sharing its width with the
        * logo. */}
      <PrintHeader settings={settings} />

      {day !== undefined && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#1a1a1a',
            color: 'white',
            padding: '8px 14px',
            marginBottom: 10,
            borderRadius: 3,
            // The bar is the point of the bar: browsers drop a background when printing unless told.
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
          }}
          data-print-day-bar
        >
          <span
            style={{
              fontSize: 16,
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: 2,
              // §9.397 - **the owner: *"that K is on the next line"*.** §9.386 put `overflowWrap:
              // 'anywhere'` here *"as in the week's badge"*, and `anywhere` breaks **inside** a word:
              // PONDELO / K. The badge is a 16 % column where that is the lesser evil; this bar is
              // the width of the page, so there was no width problem until the permission invented
              // one - a flex item may shrink below its longest word once that word is breakable.
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
            data-print-day-name
          >
            {day.dayName}
          </span>
          {fullDate !== '' && (
            <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.75, whiteSpace: 'nowrap' }}>
              {fullDate}
            </span>
          )}
        </div>
      )}

      {/* Legacy centres one day's meals in a 500 px column - a full-width line of dishes on A4 is
        * a line nobody reads to the end. */}
      <div style={{ maxWidth: 500, margin: '0 auto' }}>
        {days.map((one) => (
          <PrintDay
            key={one.dayKey}
            day={one}
            settings={settings}
            weekStartDate={week.startDate}
            products={products}
          />
        ))}
      </div>

      {/* Legacy's own footer line, which AGOS's template did not have. */}
      <div
        style={{
          marginTop: 20,
          paddingTop: 10,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 8,
          color: '#bbb',
        }}
        data-print-day-footer
      >
        <span>
          <T>Vyhradzujeme si právo na zmenu jedálneho lístka.</T>
        </span>
        <span>
          {settings.restaurantName === '' ? 'PizzaMajstro' : settings.restaurantName} ·{' '}
          {new Date().getFullYear()}
        </span>
      </div>
    </>
  )
}

export function LunchPrintView({
  week,
  settings,
  mode,
  dayKey,
  products = [],
}: {
  week: LunchWeek
  settings: LunchSettings
  mode: 'week' | 'day'
  dayKey?: string
  /** §9.401 - a meal's popis is its linked product's, or there is none. */
  products?: readonly LunchLinkedProduct[]
}) {
  const days =
    mode === 'day' ? week.days.filter((day) => day.dayKey === dayKey) : week.days

  /**
   * How much this day has to shrink to stay on one sheet - §9.382, FB-684.
   *
   * §9.132 and §9.377 both recorded this as unbuilt, and the reason it stayed that way is that the
   * arithmetic needs a **measurement**: `scrollHeight` wants a layout engine, which neither jsdom nor
   * print media gives. `printDayScale` is the decision and the hidden div below is the measurement.
   */
  const measured = useRef<HTMLDivElement>(null)
  const [dayScale, setDayScale] = useState(1)

  useEffect(() => {
    if (mode !== 'day') return
    const element = measured.current
    if (element === null) return
    setDayScale(printDayScale(element.scrollHeight))
  }, [week, dayKey, mode])

  // §9.377, FB-672 - **the week is legacy's document now.** The owner asked for *1:1* and it was a
  // different one: two pages with a full header each and the days as a table, against one
  // continuous flow. Day mode keeps this template, because legacy's day print is its own - with the
  // scale-to-fit §9.132 recorded as unbuilt.
  if (mode === 'week') {
    return (
      <div className="hidden print:block text-black bg-white" data-lunch-print="week">
        <style>{PRINT_PAGE_CSS}</style>
        <LunchPrintWeek
          days={week.days}
          settings={settings}
          weekStartDate={week.startDate}
          products={products}
        />
      </div>
    )
  }

  return (
    <>
      {/* §9.382 - **the measurement.** A4 portrait less legacy's 10 mm margins is 793 px, which is
        * legacy's own number; the copy inside is the content that prints, at the width it prints at.
        * Off-screen, `aria-hidden` so no screen reader walks a second menu, and `no-print` because a
        * hidden measuring sheet that printed would be the funniest possible regression. */}
      <div
        ref={measured}
        aria-hidden="true"
        className="no-print"
        data-print-measure={PRINT_MEASURE_WIDTH_PX}
        style={{
          position: 'fixed',
          visibility: 'hidden',
          width: `${String(PRINT_MEASURE_WIDTH_PX)}px`,
          pointerEvents: 'none',
          zIndex: -9999,
          top: '-9999px',
          left: '-9999px',
        }}
      >
        <PrintDayContent week={week} settings={settings} days={days} products={products} />
      </div>

      <div className="hidden print:block text-black bg-white" data-lunch-print="day">
        {/* §9.132 - the printer is told nothing without these. `@page` pins A4 portrait and legacy's
          * 10 mm margin, so the sheet is the one the template was designed against rather than
          * whatever the browser defaults to; `print-color-adjust` is what keeps `#c0392b` from being
          * printed grey, since browsers drop accent colour unless asked not to. */}
        <style>{PRINT_PAGE_CSS}</style>
        {/* §9.382 - scaled to fit, legacy's `transform`/`width` pair. The width is not decoration:
          * `scale()` shrinks the box as well as its contents, so without widening it first a scaled
          * day prints in a column down the left of the page. */}
        <div
          data-print-day-scale={dayScale}
          style={{
            transformOrigin: 'top left',
            transform: `scale(${String(dayScale)})`,
            width: printDayWidth(dayScale),
          }}
        >
          <PrintDayContent week={week} settings={settings} days={days} products={products} />
        </div>
      </div>
    </>
  )
}

export { PRINT_ACCENT, sectionLabel, tt }
